import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, Check, ChevronDown, ChevronRight, Search, User } from "lucide-react";

import { memberService } from "@/entities/member";
import { orgService } from "@/entities/org-department/api/orgService";
import {
  buildDeptTree,
  searchDepartments,
  type DeptTreeNode,
} from "@/entities/org-department/lib/orgTree";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";
import { cn } from "@/shared/lib/utils";

export interface PickedMember {
  principalId: number;
  displayName: string;
}

/**
 * 人之外的一条固定项(「全部」「未分派」「取消分派」…)。
 * ★ 它不是成员,所以不走 `onChange`:选它的语义由调用方定(清筛子 / 解除分派)。
 */
export interface MemberPickerAction {
  key: string;
  label: string;
  /** 当前就停在这一档 —— 打勾,与选中的人同一种反馈。 */
  active?: boolean;
  onSelect: () => void;
}

export interface MemberTreePickerProps {
  /** 当前选中成员(null = 未选)。 */
  value: PickedMember | null;
  onChange: (member: PickedMember) => void;
  placeholder?: string;
  className?: string;
  /** 触发按钮上的字。默认 = 选中的人名 / placeholder。 */
  triggerLabel?: string;
  triggerVariant?: "outline" | "ghost";
  triggerClassName?: string;
  /** 不传 = 按钮上的字就是它的可访问名(触发器有字的场景别再加一层 aria-label 盖住它)。 */
  ariaLabel?: string;
  align?: "start" | "end";
  disabled?: boolean;
  /** 人之外的固定项,排在搜索框下面、部门树上面。 */
  actions?: MemberPickerAction[];
  /**
   * 连**没进任何部门**的员工也列出来(花名册 `/tenant/members`,与任务派发 picker 同一口)。
   *
   * ★★ 为什么要有这个开关:本地真库一照 —— 中细软 3 个在职员工里**只有 1 个**在部门里
   *   (`xb_org_membership` 一行)。只按部门树选人的话,另外两个(管理员、销售)永远选不到,
   *   而"能不能被派单"的判据在后端是**活跃租户账号**,不是"在不在部门里"。
   *   失败形态还特别安静:下拉打开了、树也在,就是那个人不在里面。
   */
  includeUnassigned?: boolean;
}

/** 部门节点:chevron 展开子部门 + 懒加载成员;点成员 = 选定(单选)。点部门名只展开,不选。 */
function PickerDeptNode({
  node,
  depth,
  selectedId,
  onPick,
  defaultExpanded = false,
}: {
  node: { id: number; name: string; children: DeptTreeNode[] };
  depth: number;
  selectedId: number | null;
  onPick: (m: PickedMember) => void;
  defaultExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const membersQ = useQuery({
    queryKey: ["org", "members", node.id],
    queryFn: () => orgService.listMembers(node.id),
    enabled: expanded,
  });
  const pad = { paddingLeft: `${depth * 14 + 4}px` };

  return (
    <div>
      <div className="flex items-center gap-0.5" style={pad}>
        <button
          type="button"
          aria-label={expanded ? "收起" : "展开"}
          onClick={() => setExpanded((v) => !v)}
          className="flex h-6 w-5 shrink-0 items-center justify-center text-muted-foreground hover:text-foreground"
        >
          <ChevronRight
            className={cn("h-3.5 w-3.5 transition-transform", expanded && "rotate-90")}
          />
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-2 rounded px-1.5 py-1.5 text-left text-sm hover:bg-muted"
        >
          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">{node.name}</span>
        </button>
      </div>
      {expanded && (
        <div>
          {node.children.map((c) => (
            <PickerDeptNode
              key={c.id}
              node={c}
              depth={depth + 1}
              selectedId={selectedId}
              onPick={onPick}
            />
          ))}
          {membersQ.isLoading && (
            <div
              className="py-1 text-xs text-muted-foreground"
              style={{ paddingLeft: `${(depth + 1) * 14 + 26}px` }}
            >
              加载成员…
            </div>
          )}
          {(membersQ.data ?? []).map((m) => {
            const active = selectedId === m.principalId;
            return (
              <button
                key={m.principalId}
                type="button"
                onClick={() => onPick({ principalId: m.principalId, displayName: m.displayName })}
                style={{ paddingLeft: `${(depth + 1) * 14 + 8}px` }}
                className={cn(
                  "flex w-full items-center gap-2 rounded py-1.5 pr-2 text-left text-sm hover:bg-muted",
                  active && "bg-primary/10 text-primary"
                )}
              >
                <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="flex-1 truncate">{m.displayName}</span>
                {active && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            );
          })}
          {!membersQ.isLoading &&
            (membersQ.data?.length ?? 0) === 0 &&
            node.children.length === 0 && (
              <div
                className="py-1 text-xs text-muted-foreground/60"
                style={{ paddingLeft: `${(depth + 1) * 14 + 26}px` }}
              >
                无子部门 / 成员
              </div>
            )}
        </div>
      )}
    </div>
  );
}

/**
 * 组织部门树单选成员选择器(D1 审计页 / 获客舱分派共用)。复用 Phase B 的 `orgTree`(buildDeptTree / searchDepartments)+
 * `orgService`;与 `OrgScopeFilterBar` 共享 lib 不共享 UI —— 后者是「归属维度筛选」(全部/只看我的/部门),
 * 本组件语义是「在组织结构里选定一个人」(无 scope chips)。触发按钮显所选成员名;展开后搜索部门 + 懒加载成员点选。
 *
 * <p>0917 批次四:获客舱「分派给…」与线索库「分派」筛子也用这一个 —— 它们原来各自手搓了一个
 * 人名下拉,同一件事在三页上是三种长相。为此加了两个可选口:{@code actions}(人之外的固定档,
 * 如「全部 / 未分派 / 取消分派」)与 {@code includeUnassigned}(把不在部门里的员工也列出来)。
 * 两个都不传时行为与原来**逐字相同**(审计页不受影响)。
 */
export function MemberTreePicker({
  value,
  onChange,
  placeholder = "选择成员",
  className,
  triggerLabel,
  triggerVariant = "outline",
  triggerClassName,
  ariaLabel,
  align = "start",
  disabled = false,
  actions = [],
  includeUnassigned = false,
}: MemberTreePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const deptsQ = useQuery({
    queryKey: ["org", "departments"],
    queryFn: () => orgService.listDepartments(),
    enabled: open,
  });
  const depts = deptsQ.data ?? [];
  const tree = buildDeptTree(depts);
  const matches = searchDepartments(depts, query);

  // ── 没进部门的那些人(见 includeUnassigned 的注释)──────────────────────
  // 两口都是 MANAGER+,与部门树同门;只在打开时拉,关着的下拉不发请求。
  const rosterQ = useQuery({
    queryKey: ["tenant", "members"],
    queryFn: () => memberService.list(),
    enabled: open && includeUnassigned,
  });
  const inDeptQ = useQuery({
    queryKey: ["org", "members", "all"],
    queryFn: () => orgService.listAllMembers(),
    enabled: open && includeUnassigned,
  });
  const inDept = new Set((inDeptQ.data ?? []).map((m) => m.principalId));
  const orphans: PickedMember[] = (rosterQ.data ?? [])
    .filter((m) => m.active && !inDept.has(m.principalId))
    .map((m) => ({ principalId: m.principalId, displayName: m.name ?? `#${m.principalId}` }))
    .filter((m) => !query || m.displayName.includes(query));

  const pick = (m: PickedMember) => {
    onChange(m);
    setOpen(false);
    setQuery("");
  };

  const runAction = (a: MemberPickerAction) => {
    a.onSelect();
    setOpen(false);
    setQuery("");
  };

  return (
    <div className={className ?? "flex items-center"}>
      <Popover
        open={open}
        onOpenChange={(o) => {
          setOpen(o);
          if (!o) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant={triggerVariant}
            disabled={disabled}
            className={cn("h-9 justify-between gap-2", triggerClassName)}
            aria-label={ariaLabel}
          >
            <span className="flex items-center gap-2">
              <User className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="max-w-[12rem] truncate">
                {triggerLabel ?? value?.displayName ?? placeholder}
              </span>
            </span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align={align} className="w-72 p-0">
          <div className="flex items-center gap-2 border-b px-2 py-1.5">
            <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索部门…"
              className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
            />
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            {/* ★ 固定项在最上面:「全部 / 未分派」这类档位是用户最常点的一下,
                让他为了点「全部」先滚过一棵部门树没有道理。 */}
            {actions.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => runAction(a)}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted",
                  a.active && "bg-primary/10 text-primary"
                )}
              >
                <span className="flex-1 truncate">{a.label}</span>
                {a.active && <Check className="h-3.5 w-3.5 shrink-0" />}
              </button>
            ))}
            {actions.length > 0 && <div className="my-1 border-t" />}
            {deptsQ.isLoading ? (
              <div className="py-4 text-center text-xs text-muted-foreground">加载部门…</div>
            ) : query ? (
              matches.length > 0 ? (
                matches.map((m) => (
                  <PickerDeptNode
                    key={m.id}
                    node={{ id: m.id, name: m.label, children: [] }}
                    depth={0}
                    selectedId={value?.principalId ?? null}
                    onPick={pick}
                    defaultExpanded
                  />
                ))
              ) : orphans.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">无匹配部门</div>
              ) : null
            ) : tree.length === 0 && orphans.length === 0 ? (
              <div className="py-4 text-center text-xs text-muted-foreground">暂无部门</div>
            ) : (
              tree.map((node) => (
                <PickerDeptNode
                  key={node.id}
                  node={node}
                  depth={0}
                  selectedId={value?.principalId ?? null}
                  onPick={pick}
                />
              ))
            )}
            {/* ★ 没进部门的员工单列一组,**不混进树里**:他们确实不属于任何部门,
                硬塞进某个部门是编组织结构;而不列出来他们就永远选不到(见 includeUnassigned)。 */}
            {includeUnassigned && orphans.length > 0 && (
              <div>
                <p className="px-2 pb-0.5 pt-2 text-2xs text-muted-foreground">未归属部门</p>
                {orphans.map((m) => {
                  const active = value?.principalId === m.principalId;
                  return (
                    <button
                      key={m.principalId}
                      type="button"
                      onClick={() => pick(m)}
                      className={cn(
                        "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted",
                        active && "bg-primary/10 text-primary"
                      )}
                    >
                      <User className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span className="flex-1 truncate">{m.displayName}</span>
                      {active && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
